import { useRef, useEffect, useCallback } from 'react';

export default function Scene3D() {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    const time = Date.now() * 0.001;

    ctx.clearRect(0, 0, w, h);

    // Floating soft particles
    for (let i = 0; i < 40; i++) {
      const px = (Math.sin(i * 0.7 + time * 0.2) * 0.5 + 0.5) * w;
      const py = (Math.cos(i * 0.5 + time * 0.15) * 0.5 + 0.5) * h;
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(253, 195, 161, ${0.12 + Math.sin(i + time) * 0.06})`;
      ctx.fill();
    }

    // Soft wireframe icosahedron
    const cx = w * 0.7;
    const cy = h * 0.5;
    const scale = Math.min(w, h) * 0.22;
    const rotX = time * 0.1;
    const rotY = time * 0.14;

    const phi = (1 + Math.sqrt(5)) / 2;
    const rawVerts = [
      [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
      [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
      [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
    ];

    const edges = [
      [0,1],[0,5],[0,7],[0,10],[0,11],[1,5],[1,7],[1,8],[1,9],
      [2,3],[2,4],[2,6],[2,10],[2,11],[3,4],[3,6],[3,8],[3,9],
      [4,5],[4,9],[4,11],[5,9],[5,11],[6,7],[6,8],[6,10],
      [7,8],[7,10],[8,9],[10,11],
    ];

    const project = (v) => {
      let [x, y, z] = v;
      const cy1 = x * Math.cos(rotY) - z * Math.sin(rotY);
      const cz1 = x * Math.sin(rotY) + z * Math.cos(rotY);
      x = cy1; z = cz1;
      const cy2 = y * Math.cos(rotX) - z * Math.sin(rotX);
      const cz2 = y * Math.sin(rotX) + z * Math.cos(rotX);
      y = cy2; z = cz2;
      const perspective = 4 / (4 + z);
      return [cx + x * scale * perspective, cy + y * scale * perspective, z];
    };

    const projected = rawVerts.map(project);

    ctx.lineWidth = 1;
    edges.forEach(([a, b]) => {
      const pa = projected[a];
      const pb = projected[b];
      const avgZ = (pa[2] + pb[2]) / 2;
      const opacity = 0.06 + (avgZ + 2) * 0.03;
      ctx.beginPath();
      ctx.moveTo(pa[0], pa[1]);
      ctx.lineTo(pb[0], pb[1]);
      ctx.strokeStyle = `rgba(253, 195, 161, ${Math.max(0.04, Math.min(0.18, opacity))})`;
      ctx.stroke();
    });

    projected.forEach(([x, y, z]) => {
      const opacity = 0.12 + (z + 2) * 0.06;
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(253, 195, 161, ${Math.max(0.08, Math.min(0.3, opacity))})`;
      ctx.fill();
    });

    // Soft orbiting ring
    const ringCx = w * 0.3;
    const ringCy = h * 0.5;
    const ringScale = Math.min(w, h) * 0.15;
    ctx.beginPath();
    for (let i = 0; i <= 200; i++) {
      const t = (i / 200) * Math.PI * 2;
      const r = 1 + 0.3 * Math.sin(3 * t + time * 0.4);
      let x = r * Math.cos(2 * t);
      let y = r * Math.sin(2 * t);
      let z = 0.4 * Math.cos(3 * t + time * 0.4);
      const ry = x * Math.cos(-time * 0.08) - z * Math.sin(-time * 0.08);
      const rz = x * Math.sin(-time * 0.08) + z * Math.cos(-time * 0.08);
      x = ry; z = rz;
      const rx = y * Math.cos(time * 0.06) - z * Math.sin(time * 0.06);
      y = rx;
      const px = ringCx + x * ringScale;
      const py = ringCy + y * ringScale;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.strokeStyle = 'rgba(253, 195, 161, 0.08)';
    ctx.lineWidth = 0.8;
    ctx.stroke();

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [animate]);

  return (
    <div className="hero-canvas-container" data-testid="hero-3d-canvas">
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
}
